import { ReactNode, createContext, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { match } from "ts-pattern";

export enum ETab {
	VOTES = "VOTES",
	ACTIONS = "ACTIONS",
	FORUM = "FORUM",
	CASTING = "CASTING",
	MEDIA = "MEDIA",
	TEAM = "TEAM",
}

export type ITabContext = {
	activeTab: ETab;
	setActiveTab: (tab: ETab) => void;
	isActiveTab: (tab: ETab) => boolean;
	isTabEnabled: (tab: ETab) => boolean;
};

type IProps = {
	enabledTabs?: ETab[];
	defaultTab?: ETab;
	children: ReactNode;
};

export const TabContext = createContext<ITabContext>(undefined!);

export function TabProvider(props: IProps) {
	const { activeTab, setActiveTab, isTabEnabled } = useTabSearchParams(props);

	return (
		<TabContext.Provider
			value={{
				activeTab,
				setActiveTab,
				isActiveTab: (tab: ETab) => activeTab === tab,
				isTabEnabled,
			}}>
			{props.children}
		</TabContext.Provider>
	);
}

const useTabSearchParams = (props: Omit<IProps, "children">) => {
	const [searchParams, setSearchParams] = useSearchParams();

	const defaultTab = props.defaultTab ?? ETab.ACTIONS;

	const isTabEnabled = useCallback(
		(tab: ETab) => {
			return !props.enabledTabs || props.enabledTabs.includes(tab);
		},
		[props.enabledTabs],
	);

	const getTabFromUrl = useCallback((): ETab => {
		const tab = searchParams.get("tab");

		return match(tab?.toUpperCase())
			.with("ACTIONS", () => (isTabEnabled(ETab.ACTIONS) ? ETab.ACTIONS : defaultTab))
			.with("FORUM", () => (isTabEnabled(ETab.FORUM) ? ETab.FORUM : defaultTab))
			.with("CASTING", () => (isTabEnabled(ETab.CASTING) ? ETab.CASTING : defaultTab))
			.with("MEDIA", () => (isTabEnabled(ETab.MEDIA) ? ETab.MEDIA : defaultTab))
			.with("TEAM", () => (isTabEnabled(ETab.TEAM) ? ETab.TEAM : defaultTab))
			.otherwise(() => defaultTab);
	}, [defaultTab, isTabEnabled, searchParams]);

	const [activeTab, setActiveTab] = useState<ETab>(getTabFromUrl);

	const setTabInUrl = useCallback(
		(tab: ETab) => {
			searchParams.set("tab", ETab[tab].toLowerCase());
			setSearchParams(searchParams);
			setActiveTab(tab);
		},
		[searchParams, setSearchParams],
	);

	useEffect(() => {
		const tab = getTabFromUrl();

		if (tab !== activeTab) {
			setActiveTab(tab);
		}
	}, [activeTab, getTabFromUrl]);

	return {
		activeTab,
		setActiveTab: setTabInUrl,
		isTabEnabled,
	};
};
